"use client";

/**
 * L'ÉCRAN « QONTO » DES RÉGLAGES.
 *
 * MyFacture lit les crédits du compte Qonto de la société, avec sa clé API, et
 * détecte les paiements de TOUTES les factures : sûr → « Payée » tout seul ;
 * doute → une proposition à confirmer ici d'un clic. Le PDF de la facture est
 * joint comme justificatif à l'opération Qonto. La clé secrète se saisit ici
 * et n'y revient jamais.
 */

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { useData } from "@/components/data-provider";
import { etatQonto, enregistrerCleQonto, synchroniserQontoMaintenant, oublierCleQonto, confirmerPaiementQonto, ecarterPropositionQonto, type EtatQonto } from "@/lib/actions/qonto";
import type { RapportQonto } from "@/lib/qonto/synchro";
import { toast } from "sonner";
import { Landmark, ChevronLeft, Loader2, RefreshCw, Unlink, CheckCircle2, AlertCircle, Info, LogIn, Check, X } from "lucide-react";

const euros = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const ecart = (n: number) => (n === 0 ? "aucun écart" : `${n > 0 ? "gain" : "perte"} de change ${euros(Math.abs(n))}`);

function Rapport({ rapport }: { rapport: RapportQonto }) {
  const pointees = rapport.pointees ?? [];
  const justificatifs = rapport.justificatifs ?? { joints: 0, erreurs: [] };
  return (
    <div className="space-y-2 text-sm">
      {rapport.erreur && <p className="text-red-400">{rapport.erreur}</p>}
      {!rapport.erreur && (
        <p className="text-muted-foreground">
          {rapport.compte ? `${rapport.compte.nom} (${rapport.compte.iban}) · ` : ""}
          {rapport.credits} crédit(s) lu(s) · {rapport.aPayer} facture(s) en attente · {pointees.length} payée(s) à ce passage
          {justificatifs.joints ? ` · ${justificatifs.joints} justificatif(s) joint(s)` : ""}
        </p>
      )}
      {pointees.length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc list-inside">
          {pointees.map((p) => (
            <li key={`${p.numero}-${p.date}`}>
              {p.numero} — reçu {euros(p.montantRecu)} le {new Date(p.date).toLocaleDateString("fr-FR")}
              {p.ecartChange ? ` · ${ecart(p.ecartChange)}` : ""}{p.raison ? ` · ${p.raison}` : ""}
            </li>
          ))}
        </ul>
      )}
      {justificatifs.erreurs.length > 0 && (
        <p className="text-xs text-amber-400">Justificatif non joint : {justificatifs.erreurs.slice(0, 3).join(" · ")}</p>
      )}
    </div>
  );
}

function AConfirmer({ rapport, societeId, onChange }: { rapport: RapportQonto; societeId: string; onChange: () => Promise<void> }) {
  const [enCours, setEnCours] = useState<string | null>(null);
  const liste = rapport.aConfirmer ?? [];
  if (liste.length === 0) return null;

  const agir = async (virementId: string, factureId: string, confirmer: boolean) => {
    setEnCours(`${virementId}:${factureId}`);
    try {
      const res = confirmer
        ? await confirmerPaiementQonto(societeId, virementId, factureId)
        : await ecarterPropositionQonto(societeId, virementId, factureId);
      if (!res.success) toast.error(res.error);
      else toast.success(confirmer ? "Facture passée à « Payée »." : "Proposition écartée.");
      await onChange();
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="glass-card p-5 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-3">
      <h3 className="text-sm font-semibold text-foreground">À confirmer ({liste.length})</h3>
      <p className="text-xs text-muted-foreground">MyFacture n'est pas sûr de ces paiements : confirmez pour passer la facture à « Payée », ou écartez la proposition.</p>
      <ul className="space-y-2">
        {liste.map((p) => {
          const cle = `${p.virementId}:${p.factureId}`;
          return (
            <li key={cle} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3 text-sm">
              <div className="flex-1 min-w-[220px]">
                <p className="font-medium text-foreground">
                  {euros(p.montant)} reçu le {new Date(p.date).toLocaleDateString("fr-FR")} — {p.libelle}
                </p>
                <p className="text-xs text-muted-foreground">
                  → Facture {p.numero} · {p.client} · reste dû {euros(p.resteDu)} · {p.raison}
                </p>
              </div>
              <button type="button" disabled={enCours !== null} onClick={() => agir(p.virementId, p.factureId, true)} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                {enCours === cle ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Confirmer
              </button>
              <button type="button" disabled={enCours !== null} onClick={() => agir(p.virementId, p.factureId, false)} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted disabled:opacity-50">
                <X className="h-3.5 w-3.5" /> Écarter
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function QontoSettings({ onBack }: { onBack?: () => void }) {
  const { societe } = useData();
  const [etat, setEtat] = useState<EtatQonto | null>(null);
  const [identifiant, setIdentifiant] = useState("");
  const [cleSecrete, setCleSecrete] = useState("");
  const [iban, setIban] = useState("");
  const [enCours, setEnCours] = useState<"enregistrer" | "synchroniser" | "oublier" | null>(null);

  const charger = useCallback(async () => {
    if (!societe?.id) return;
    const res = await etatQonto(societe.id);
    if (res.success) {
      setEtat(res.data);
      if (res.data.identifiant) setIdentifiant(res.data.identifiant);
      if (res.data.iban) setIban(res.data.iban);
    } else toast.error(res.error);
  }, [societe?.id]);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societe?.id) return;
    setEnCours("enregistrer");
    try {
      const res = await enregistrerCleQonto(societe.id, identifiant, cleSecrete, iban);
      if (res.success) {
        setCleSecrete("");
        toast.success(`Qonto connecté — compte ${res.data.compte}.`);
        await charger();
      } else toast.error(res.error);
    } finally {
      setEnCours(null);
    }
  };

  const synchroniser = async () => {
    if (!societe?.id) return;
    setEnCours("synchroniser");
    try {
      const res = await synchroniserQontoMaintenant(societe.id);
      if (!res.success) toast.error(res.error);
      else if (res.data.erreur) toast.error(res.data.erreur);
      else toast.success(`${res.data.pointees.length} facture(s) passée(s) à « Payée »${res.data.aConfirmer.length ? `, ${res.data.aConfirmer.length} à confirmer` : ""}.`);
      await charger();
    } finally {
      setEnCours(null);
    }
  };

  const oublier = async () => {
    if (!societe?.id) return;
    if (!window.confirm("Oublier la clé Qonto ? Les paiements déjà pointés restent ; plus aucun ne sera détecté.")) return;
    setEnCours("oublier");
    try {
      const res = await oublierCleQonto(societe.id);
      if (res.success) { setIdentifiant(""); setIban(""); toast.success("Clé oubliée."); await charger(); }
      else toast.error(res.error);
    } finally {
      setEnCours(null);
    }
  };

  const connecte = etat?.connecte ?? false;

  return (
    <div className="space-y-6">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-400 transition-colors font-medium">
          <ChevronLeft className="h-5 w-5" /> Réglages
        </button>
      )}

      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-violet-500/10"><Landmark className="h-6 w-6 text-violet-400" /></div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Qonto — paiements reçus</h2>
          <p className="text-xs text-muted-foreground">
            MyFacture lit chaque jour les virements reçus sur le compte Qonto de la société, passe à « Payée » les factures dont le paiement est arrivé et joint leur PDF à l'opération.
          </p>
        </div>
      </div>

      <div className={`glass-card p-4 rounded-xl border ${connecte ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
        <div className="flex items-start gap-3">
          {connecte ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" /> : <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />}
          <div className="text-sm flex-1 space-y-2">
            {etat === null ? (
              <p className="text-muted-foreground">Chargement…</p>
            ) : connecte ? (
              <>
                <p className="font-medium text-foreground">Qonto connecté{etat.iban ? ` — ${etat.iban.slice(0, 4)} •••• ${etat.iban.slice(-4)}` : ""}</p>
                <p className="text-muted-foreground">
                  Dernier passage : {etat.derniereSynchro ? new Date(etat.derniereSynchro).toLocaleString("fr-FR") : "jamais"}.
                </p>
                {etat.rapport && <Rapport rapport={etat.rapport} />}
              </>
            ) : (
              <>
                <p className="font-medium text-foreground">Aucune clé enregistrée</p>
                <p className="text-muted-foreground">Aucun paiement n'est détecté tant que la clé API Qonto n'est pas enregistrée ci-dessous.</p>
              </>
            )}
          </div>
        </div>
      </div>

      {etat?.rapport && societe?.id && <AConfirmer rapport={etat.rapport} societeId={societe.id} onChange={charger} />}

      {connecte && (
        <Link href="/rattachement" className="glass-card flex items-center justify-between gap-3 p-4 rounded-xl border border-border hover:bg-muted">
          <span className="text-sm text-foreground">
            <strong>Rattachement</strong> — {(etat?.rapport?.nonRattaches ?? []).length} virement(s) reçu(s) à rattacher à une facture
          </span>
          <span className="text-sm text-blue-500 font-medium">Ouvrir</span>
        </Link>
      )}

      <form onSubmit={enregistrer} className="glass-card p-5 rounded-xl border border-border space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Clé API Qonto</h3>
        <p className="text-xs text-muted-foreground">
          Dans Qonto, sur la société concernée : <strong>Paramètres → Intégrations et partenariats → Clé API</strong>. Recopiez l'identifiant et la clé secrète. MyFacture vérifie la connexion avant d'enregistrer ; la clé est chiffrée et ne s'affiche plus jamais. Si votre accès Qonto couvre plusieurs comptes, indiquez l'IBAN de celui de cette société.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Identifiant</span>
            <input type="text" autoComplete="off" value={identifiant} onChange={(e) => setIdentifiant(e.target.value)} required className="w-full h-9 px-3 rounded-lg border border-border bg-background text-foreground" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Clé secrète{connecte ? " (pour la changer)" : ""}</span>
            <input type="password" autoComplete="new-password" value={cleSecrete} onChange={(e) => setCleSecrete(e.target.value)} required className="w-full h-9 px-3 rounded-lg border border-border bg-background text-foreground" />
          </label>
          <label className="space-y-1 text-sm sm:col-span-2">
            <span className="text-muted-foreground">IBAN du compte (facultatif — sinon le compte principal)</span>
            <input type="text" autoComplete="off" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="FR76 …" className="w-full h-9 px-3 rounded-lg border border-border bg-background text-foreground font-mono" />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={enCours !== null} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {enCours === "enregistrer" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {connecte ? "Changer de clé" : "Se connecter"}
          </button>
          {connecte && (
            <>
              <button type="button" onClick={synchroniser} disabled={enCours !== null} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-50">
                {enCours === "synchroniser" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Pointer maintenant
              </button>
              <button type="button" onClick={oublier} disabled={enCours !== null} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-red-500/40 text-red-400 text-sm font-medium hover:bg-red-500/10 disabled:opacity-50">
                {enCours === "oublier" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                Oublier la clé
              </button>
            </>
          )}
        </div>
      </form>

      <div className="glass-card p-5 rounded-xl border border-border space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Info className="h-4 w-4 text-blue-400" />Ce que fait MyFacture</div>
        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1.5">
          <li>Il lit les virements reçus. La seule chose qu'il écrit dans Qonto, c'est le PDF de la facture, joint comme justificatif à l'opération qui la paie. Aucun virement, aucune modification d'opération.</li>
          <li>Sûr = automatique : le virement porte exactement le reste dû ET cite le numéro de la facture ou nomme le client (une seule facture possible). La facture passe à « Payée » toute seule.</li>
          <li>Doute = à confirmer : montant exact sans nom ni numéro, ou numéro cité avec un autre montant. La proposition apparaît ci-dessus.</li>
          <li>Autofactures Meta : un virement PayPal dont la référence nomme Facebook, à moins de 8 % du montant ; la facture garde le taux BCE et l'écart de change est affiché.</li>
          <li>Un même virement ne paie jamais deux factures.</li>
        </ul>
      </div>
    </div>
  );
}
